import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, EnvelopeSimple, Phone, NavigationArrow } from "@phosphor-icons/react";
import StonLogo from "@/components/StonLogo";

const MOODBOARD_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_track-manufacturing/artifacts/ryk9mk4w_ChatGPT%20Image%20Aug%205%2C%202026%2C%2009_08_01%20PM.png";

const SHOWROOM_MAPS_URL = "https://maps.app.goo.gl/fk8f94he2RdPCUFs9";
const SUPPORT_EMAIL = "Divyanshu.fdr@stonlink.com";
const SUPPORT_PHONE_DISPLAY = "+971 50 735 8704";
const SUPPORT_PHONE_TEL = "+971507358704";
const SUPPORT_ADDRESS = "Ibn Battuta office building, Dubai, UAE.";

function ContactCard({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <div className="flex items-start gap-4 p-5 bg-white/95 backdrop-blur-md border border-zinc-900/10 shadow-[0_10px_40px_-16px_rgba(60,40,10,0.35)] transition-all duration-200 hover:border-[#8B4513]/40 hover:-translate-y-0.5">
      <div className="shrink-0 w-11 h-11 grid place-items-center bg-[#8B4513]/10 text-[#8B4513]">
        <Icon size={22} weight="duotone" />
      </div>
      <div className="min-w-0">
        <div className="mt-tiny text-zinc-500">{label}</div>
        <div className="mt-1 text-zinc-900 font-medium break-words">{value}</div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId} className="block">
        {inner}
      </a>
    );
  }
  return <div data-testid={testId}>{inner}</div>;
}

export default function Support() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen relative overflow-hidden text-zinc-900"
      data-testid="support-page"
      style={{
        backgroundImage: `url('${MOODBOARD_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Warm cream overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f7f1e6]/90 via-[#efe6d4]/78 to-[#e6d9bc]/60 pointer-events-none" />

      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between p-6 md:p-10">
          <StonLogo size={40} subtitle="Building Materials" className="text-zinc-900" data-testid="support-brand-logo" />
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="support-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#8B4513] transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back to sign in
          </button>
        </header>

        {/* Body */}
        <main className="flex-1 flex flex-col items-center px-6 pb-16">
          <div className="w-full max-w-3xl">
            <div className="mt-tiny text-[#8B4513]">We&apos;re here to help</div>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-zinc-900 mt-3 leading-[1.03]">
              Support &amp; Contact
            </h1>
            <p className="mt-5 text-zinc-700 text-lg leading-relaxed max-w-xl">
              Questions about an order, a quotation, or a material spec? Reach the Ston Link team
              directly, or visit our Dubai showroom.
            </p>

            {/* Visit showroom CTA */}
            <a
              href={SHOWROOM_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="support-showroom-button"
              className="group mt-10 w-full flex items-center justify-between gap-4 bg-zinc-900 hover:bg-zinc-800 text-white px-7 py-6 md:py-7 transition-colors duration-200 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.5)]"
            >
              <span className="flex items-center gap-4">
                <MapPin size={30} weight="duotone" className="text-[#e6b877]" />
                <span className="text-left">
                  <span className="block mt-tiny text-white/50">Dubai, UAE</span>
                  <span className="block font-display text-xl md:text-2xl font-black tracking-tight">
                    Visit our Showroom
                  </span>
                </span>
              </span>
              <NavigationArrow
                size={26}
                weight="fill"
                className="text-white/70 group-hover:text-white group-hover:translate-x-1 transition-all"
              />
            </a>

            {/* Contact details */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ContactCard
                icon={EnvelopeSimple}
                label="Support email"
                value={SUPPORT_EMAIL}
                href={`mailto:${SUPPORT_EMAIL}`}
                testId="support-email"
              />
              <ContactCard
                icon={Phone}
                label="Phone"
                value={SUPPORT_PHONE_DISPLAY}
                href={`tel:${SUPPORT_PHONE_TEL}`}
                testId="support-phone"
              />
              <ContactCard
                icon={MapPin}
                label="Showroom address"
                value={SUPPORT_ADDRESS}
                href={SHOWROOM_MAPS_URL}
                testId="support-address"
              />
              <div className="sm:col-span-1 flex items-center p-5 border border-dashed border-zinc-900/15">
                <p className="text-sm text-zinc-600 leading-relaxed">
                  Our team typically responds within one business day, Sunday–Thursday.
                </p>
              </div>
            </div>

            <div className="mt-12 mt-tiny text-zinc-600/70">© 2026 Ston Link — Building Materials</div>
          </div>
        </main>
      </div>
    </div>
  );
}
